import { motion } from 'framer-motion'
import {
  TrendingUp,
  Zap,
  Languages,
  Calculator,
  AlertCircle,
  FileDown,
} from 'lucide-react'
import { useLanguage } from '@/context/LanguageContext'
import { useAnimateOnScroll } from '@/hooks/useAnimateOnScroll'
import { Badge } from '@/components/ui/Badge'

const featureIcons = [
  TrendingUp,
  Zap,
  Languages,
  Calculator,
  AlertCircle,
  FileDown,
]

const featureColors = [
  'from-profit-500/20 to-profit-600/20',
  'from-gold-500/20 to-gold-600/20',
  'from-blue-500/20 to-blue-600/20',
  'from-purple-500/20 to-purple-600/20',
  'from-orange-500/20 to-orange-600/20',
  'from-maple-500/20 to-maple-600/20',
]

const iconColors = [
  'text-profit-400',
  'text-gold-400',
  'text-blue-400',
  'text-purple-400',
  'text-orange-400',
  'text-maple-500',
]

export function Features() {
  const { t } = useLanguage()
  const { ref, isVisible } = useAnimateOnScroll({ threshold: 0.1 })

  return (
    <section id="features" className="relative py-24 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-midnight-950" />
      <div className="absolute inset-0 grid-pattern opacity-30" />

      {/* Gradient Accent */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] opacity-30"
        style={{
          background: 'radial-gradient(ellipse, rgba(247, 208, 70, 0.15) 0%, transparent 70%)',
          filter: 'blur(60px)',
        }}
      />

      <div className="container mx-auto px-4 relative z-10" ref={ref}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-100 mb-4">
            {t.features.headline}
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            {t.features.subheadline}
          </p>
        </motion.div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {t.features.items.map((feature, index) => {
            const Icon = featureIcons[index]
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                animate={isVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.1 + index * 0.1 }}
                className="feature-card glass-card p-8 group"
              >
                {/* Icon */}
                <div
                  className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${featureColors[index]} flex items-center justify-center mb-6 feature-icon transition-all duration-300`}
                >
                  <Icon className={`w-7 h-7 ${iconColors[index]}`} />
                </div>

                {/* Content */}
                <h3 className="text-xl font-bold text-slate-100 mb-3">
                  {feature.title}
                </h3>
                <p className="text-slate-400 mb-4 leading-relaxed">
                  {feature.description}
                </p>

                {/* Highlight Badge */}
                <Badge variant="gold" className="text-xs">
                  {feature.highlight}
                </Badge>
              </motion.div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
